<template>
  <div class="booking-divider booking-divider-right">
    <div class="booking-content">
      <display-banner/>
      <template v-if="loadingPets">
        <span class="inline-loader d-md-block d-none h-100"></span>
        <span class="inline-loader d-md-none d-block"></span>
      </template>
      <template v-else-if="pets.length > 0">
        <div class="booking-divider-title text-header-color">Your pet details</div>
        <div class="timeslots">
          <validation-message v-if="message" :message="message" class="mb-2"/>
          <pet-select-item v-for="pet in pets" :key="pet.id" :pet="pet" @petSelected="onPetSelected"/>
          <pet-select-all v-if="showAllOption" :all-selected="allSelected" @allPetsSelected="onAllPetsSelected"/>
        </div>
      </template>
      <div v-else-if="noPets" class="font-size-h2 text-header-color mt-3 mt-md-1 px-4 text-center">NO REGISTERED PETS</div>
    </div>
    <div class="fixed-buttons" v-if="pets.length > 0">
      <button class="btn btn-secondary w-100" @click="next" :disabled="loadingPets">Next</button>
    </div>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import { PetCacheMixin } from '@/mixins/Booking/PetCacheMixin'
import { PreferredResourceCacheMixin } from '@/mixins/Booking/PreferredResourceCacheMixin'
import { ClientUserCheckingMixin } from '@/mixins/Booking/ClientUserCheckingMixin'
import { SiteBookingSettingMixin } from '@/mixins/Booking/SiteBookingSettingMixin'
import { PetDurationCacheMixin } from '@/mixins/Booking/PetDurationCacheMixin'
import { OpenSlotCacheMixin } from '@/mixins/Booking/OpenSlotCacheMixin'
import PetSelectItem from './PetSelectItem.vue'
import PetSelectAll from './PetSelectAll.vue'
import DisplayBanner from '../DisplayBanner.vue'
import ValidationMessage from '@/components/ErrorValidation/ValidationMessage.vue'

export default {
  name: 'PetSelect',
  mixins: [ClientUserCheckingMixin, PetCacheMixin, PreferredResourceCacheMixin, SiteBookingSettingMixin, PetDurationCacheMixin, OpenSlotCacheMixin],
  data () {
    return {
      message: '',
      isValid: false
    }
  },
  computed: {
    ...mapState('BookingUpload', [
      'selectedPets',
      'defaultDuration'
    ]),
    noPets () {
      return this.attempted && this.pets.length < 1 && !this.loadingPets
    },
    maxSelectablePets () {
      const max = this.siteBookingSettingMaxRegisteredPets
      return this.openSlotBooking ? Math.min(max, this.availableSlotCount) : max
    },
    showAllOption () {
      return this.pets.length <= this.maxSelectablePets && this.pets.length > 1
    },
    allPetIds () {
      return this.pets.map(pet => pet.id)
    },
    selectedPetIds () {
      return this.selectedPets.length > 0 ? this.selectedPets.map(p => p.id) : []
    },
    allSelected () {
      return (
        this.showAllOption &&
        this.selectedPetIds.length === this.allPetIds.length &&
        this.selectedPetIds.every(id => this.allPetIds.includes(id))
      )
    }
  },
  watch: {
    selectedPets: {
      deep: true,
      handler () {
        this.updateSelectedPetsAppointmentDurations()
      }
    },
    petDurations () {
      this.updateSelectedPetsAppointmentDurations()
    }
  },
  methods: {
    ...mapMutations('BookingUpload', [
      'setSelectedPets',
      'clearSelectedPets',
      'updatePetAppointmentDuration'
    ]),
    petProfileDurationMinutes (pet) {
      const profileDuration = this.petDurations.find(d => d.petId === pet.id)
      return profileDuration ? profileDuration.hours * 60 + profileDuration.minutes : 0
    },
    computePetAppointmentDuration (pet) {
      const profileMinutes = this.petProfileDurationMinutes(pet)
      const fixedDuration = profileMinutes > 0 ? profileMinutes : this.defaultDuration
      if (this.siteBookingSettingServiceBasedDurationEnabled) {
        const serviceDuration = (pet.serviceItems ?? []).reduce((sum, item) => sum + (item.durationMinutes || 0), 0)
        return serviceDuration > 0 ? serviceDuration : this.defaultDuration
      }
      return fixedDuration
    },
    updateSelectedPetsAppointmentDurations () {
      this.selectedPets.forEach(pet => {
        this.updatePetAppointmentDuration({ petId: pet.id, duration: this.computePetAppointmentDuration(pet) })
      })
    },
    onPetSelected (value) {
      const existing = this.selectedPets.find(sp => sp.petId === value.petId)
      if (existing) {
        if (this.allSelected) {
          this.setSelectedPets([existing])
        } else {
          this.setSelectedPets(this.selectedPets.filter(sp => sp.petId !== value.petId))
        }
        this.message = ''
      } else {
        if (this.selectedPets.length < this.maxSelectablePets) {
          this.setSelectedPets([...this.selectedPets, value])
        } else {
          const updatedPets = [...this.selectedPets.slice(1), value]
          this.setSelectedPets(updatedPets)
          this.message = this.message ? '' : `You can only select a maximum of ${this.maxSelectablePets} pet(s).`
        }
      }
    },
    onAllPetsSelected (value) {
      if (this.allSelected) {
        this.clearSelectedPets()
      } else {
        this.setSelectedPets(value)
      }
    },
    next () {
      this.check()
      if (this.isValid) {
        this.$emit('next')
        this.$emit('toLastPet', false)
      }
    },
    check () {
      this.isValid = this.selectedPets.length > 0
      this.message = this.isValid ? '' : 'Please select pet(s)'
    }
  },
  components: {
    'pet-select-item': PetSelectItem,
    'pet-select-all': PetSelectAll,
    'display-banner': DisplayBanner,
    'validation-message': ValidationMessage
  }
}
</script>
